"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import {
  ChevronLeft,
  Clock,
  Users,
  CalendarDays,
  MapPin,
  ShieldCheck,
  DollarSign,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { PhoneInput } from "@/components/ui/phone-input-with-images";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getDefaultCountryFromTimezone } from "@/lib/constants/timezone-country-map";
import { LanguageSelector } from "@/components/booking/LanguageSelector";
import { BookingGapWarningModal } from "@/components/booking/BookingGapWarningModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { SheetVariantFour } from "./SheetVariantFour";
import { ModalVariantThree } from "./ModalVariantThree";
import { Show } from "@/lib/mockData";
import { useTranslation } from "react-i18next";
import { PricingService } from "@/lib/pricing/pricing-service-unified";
import { createClient } from "@/lib/supabase/client";
import posthog from "posthog-js";
import { toast } from "sonner";
import { formatTimeDisplay } from "@/lib/utils/time";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { useBookingPixelTracking } from "@/lib/hooks/useFacebookPixel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Phone number formatting helpers
function formatPhoneToE164(phone: string): string {
  if (!phone || typeof phone !== "string") {
    return "";
  }
  let formattedPhone = phone.trim();
  // If empty, return empty
  if (!formattedPhone) {
    return "";
  }
  // If phone already starts with +, use as is (assume it's already in E.164 format)
  if (formattedPhone.startsWith("+")) {
    // Clean up any non-digit characters except the leading +
    return "+" + formattedPhone.slice(1).replace(/\D/g, "");
  }
  // For numbers without country code, add + and clean up
  // Remove all non-digit characters
  const digitsOnly = formattedPhone.replace(/\D/g, "");
  // Add + prefix for E.164 format
  return "+" + digitsOnly;
}

// Phone validation helper - only validates, doesn't modify
function validatePhone(phone: string): {
  isValid: boolean;
  error?: string;
} {
  // Handle empty input
  if (!phone || typeof phone !== "string") {
    return {
      isValid: true, // Empty is valid (optional field)
      error: undefined,
    };
  }

  // Clean the input - only for validation, not modification
  const cleaned = phone.trim();

  // Handle empty input after trimming
  if (!cleaned) {
    return {
      isValid: true, // Empty is valid (optional field)
      error: undefined,
    };
  }

  // Extract digits only for pattern checking
  const digitsOnly = cleaned.replace(/[^\d]/g, "");

  // Check for obviously invalid patterns
  if (
    digitsOnly.match(/^0+$/) || // All zeros
    digitsOnly.match(/^1+$/) || // All ones
    digitsOnly.match(/^2+$/) || // All twos
    digitsOnly.match(/^3+$/) || // All threes
    digitsOnly.match(/^4+$/) || // All fours
    digitsOnly.match(/^5+$/) || // All fives
    digitsOnly.match(/^6+$/) || // All sixes
    digitsOnly.match(/^7+$/) || // All sevens
    digitsOnly.match(/^8+$/) || // All eights
    digitsOnly.match(/^9+$/) || // All nines
    digitsOnly === "123456789" || // Sequential test number
    digitsOnly === "1234567890" // Another test pattern
  ) {
    return {
      isValid: false,
      error: "Please enter a valid phone number",
    };
  }

  // Check length
  if (digitsOnly.length < 7) {
    return {
      isValid: false,
      error: "Phone number is too short",
    };
  }

  if (digitsOnly.length > 15) {
    return {
      isValid: false,
      error: "Phone number is too long",
    };
  }

  // Basic format check - allow various formats
  // Allow: +, spaces, dashes, parentheses, and digits
  const basicFormatRegex = /^[\d\s\-\(\)\+]+$/;
  if (!basicFormatRegex.test(cleaned)) {
    return {
      isValid: false,
      error: "Phone number contains invalid characters",
    };
  }

  return {
    isValid: true,
  };
}

// Import ShowData interface from ModalVariantThree
interface ShowData {
  id: number;
  name: string;
  subtitle: string;
  description: string;
  startTime: string;
  duration: string;
  price: number;
  currency: string;
  minimumCharge: number;
  availableLanguages: string[];
  groupSize: string;
  min_participants: number;
  max_participants: number;
  min_charge_participants?: number | null;
  language: string;
  has_booking_conflict: boolean;
  videos?: string[];
  images?: string[];
  faqs?: Array<{
    id: number;
    question: string | null;
    answer: string | null;
  }>;
  venue_info?: {
    name: string | null;
    description: string | null;
  };
}

interface BookingSummaryEditPricing {
  pricePerPerson: number;
  participants: number;
  subtotal: number;
  vatAmount: number;
  total: number;
  originalTotal?: number;
  difference?: number;
}

interface BookingSummaryEditConfig {
  originalDetails: {
    showName: string;
    timeSlot: string;
    date: string;
  };
  pricing: BookingSummaryEditPricing;
  contactInfo?: {
    name?: string;
    email?: string;
    phone?: string;
    companyName?: string;
    readOnly?: boolean;
  };
  onParticipantsChange?: (participants: number) => void;
  onConfirm: () => Promise<void>;
  isProcessing: boolean;
}

interface BookingSummaryPageProps {
  showData: any; // Show data from API
  venueInfo: any; // Venue information
  venueDetails: any; // Venue details with participant limits
  selectedDate: Date;
  groupSize: number;
  bookingType: "private" | "company";
  time: string;
  onBack: () => void;
  availableLanguages?: string[]; // Available languages for this venue
  editConfig?: BookingSummaryEditConfig;
  currentUserEmail?: string | null;
}

// Language code to display name mapping
const languageNames: Record<string, string> = {
  en: "English",
  sv: "Swedish",
  no: "Norwegian",
  da: "Danish",
  fr: "French",
  de: "German",
  fi: "Finnish",
  es: "Spanish",
  it: "Italian",
  nl: "Dutch",
  pt: "Portuguese",
  ru: "Russian",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
};

export function BookingSummaryPage({
  showData,
  venueInfo,
  venueDetails,
  selectedDate,
  groupSize,
  bookingType,
  time,
  onBack,
  availableLanguages,
  editConfig,
  currentUserEmail,
}: BookingSummaryPageProps) {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { trackInitiateCheckout } = useBookingPixelTracking();
  const isEditMode = Boolean(editConfig);
  const isLoggedIn = Boolean(currentUserEmail);

  // Personal/Contact Information - pre-fill email if logged in
  const [email, setEmail] = useState(
    currentUserEmail || editConfig?.contactInfo?.email || ""
  );
  const [name, setName] = useState(editConfig?.contactInfo?.name ?? "");
  const [phone, setPhone] = useState(editConfig?.contactInfo?.phone ?? "");
  const [phoneError, setPhoneError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Company Information (for company bookings - simplified)
  const [companyName, setCompanyName] = useState(
    editConfig?.contactInfo?.companyName ?? "",
  );

  // Other states - in edit mode use current booking language, otherwise no pre-selection
  const [language, setLanguage] = useState(showData.language || "");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [currentGroupSize, setCurrentGroupSize] = useState(groupSize);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const contactReadOnly = Boolean(editConfig?.contactInfo?.readOnly);

  // Gap warning modal state
  const [gapWarningOpen, setGapWarningOpen] = useState(false);
  const [gapWarningData, setGapWarningData] = useState<{
    showName: string;
    date: string;
    startTime: string;
    endTime: string;
    timeGap: number;
  } | null>(null);

  // Sort available languages with venue default first
  const sortedAvailableLanguages = useMemo(() => {
    if (!availableLanguages || availableLanguages.length === 0) return [];
    const defaultLang = showData.default_language || "en";
    return [...availableLanguages].sort((a, b) => {
      if (a === defaultLang) return -1;
      if (b === defaultLang) return 1;
      return 0;
    });
  }, [availableLanguages, showData.default_language]);

  const languageDisplayNames = useMemo(() => {
    if (typeof Intl === "undefined" || typeof Intl.DisplayNames === "undefined") {
      return null;
    }
    const locale = i18n.resolvedLanguage || i18n.language || "en";
    try {
      return new Intl.DisplayNames([locale], { type: "language" });
    } catch {
      return null;
    }
  }, [i18n.language, i18n.resolvedLanguage]);

  const getLanguageLabel = (langCode: string) => {
    const normalized = langCode.split("-")[0].toLowerCase();
    const displayName = languageDisplayNames?.of(normalized) ||
      languageNames[normalized] ||
      normalized.toUpperCase();

    // Capitalize first letter (Intl.DisplayNames returns lowercase in some locales)
    return displayName.charAt(0).toUpperCase() + displayName.slice(1);
  };

  // Validate phone in real-time
  const validatePhoneInput = (phoneValue: string): boolean => {
    if (!phoneValue) {
      setPhoneError("");
      return true; // Empty is valid (optional field)
    }

    const phoneResult = validatePhone(phoneValue);

    if (!phoneResult.isValid) {
      setPhoneError(phoneResult.error || "Invalid phone number");
      return false;
    }

    setPhoneError("");
    return true;
  };

  // Validate email in real-time
  const validateEmailInput = (emailValue: string): boolean => {
    if (!emailValue) {
      setEmailError("");
      return true; // Empty is valid (optional field)
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      setEmailError("Please enter a valid email address");
      return false;
    }

    setEmailError("");
    return true;
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    // Validate in real-time
    validatePhoneInput(value);
  };


  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Validate in real-time
    validateEmailInput(value);
  };

  useEffect(() => {
    if (editConfig?.contactInfo) {
      setEmail(editConfig.contactInfo.email ?? "");
      setName(editConfig.contactInfo.name ?? "");
      setPhone(editConfig.contactInfo.phone ?? "");
      setCompanyName(editConfig.contactInfo.companyName ?? "");
      // Validate initial values
      validatePhoneInput(editConfig.contactInfo.phone ?? "");
      validateEmailInput(editConfig.contactInfo.email ?? "");
    }
  }, [editConfig]);


  useEffect(() => {
    setCurrentGroupSize(groupSize);
  }, [groupSize]);

  // Get slider limits from show data (show-specific limits take precedence over venue-wide limits)
  const minParticipants = showData.min_participants || 2;
  const maxParticipants = showData.max_participants || 12;

  // Get cancellation policy from showData (for free cancellation display)
  const cancelFreeDays = showData.cancellation_policy?.cancel_free_days || 7;
  const cancelGraceHours =
    showData.cancellation_policy?.cancel_grace_hours || 24;

  // Get participant modification rules from rule_sets (different from cancellation!)
  const partChangeFullRefundDays = showData.rule_sets?.part_change_full_refund_days || 7;
  const partChangeCutoffHours = showData.rule_sets?.part_change_cutoff_hours || 5;

  // Calculate dynamic dates for policy section based on selected show date
  const showDate = new Date(selectedDate);
  const freeCancellationDate = new Date(showDate);
  freeCancellationDate.setDate(showDate.getDate() - cancelFreeDays);

  // Fix: Use participant modification rules, not cancellation rules
  const modifyParticipantsDate = new Date(showDate);
  modifyParticipantsDate.setDate(showDate.getDate() - partChangeFullRefundDays);

  // Check if modification deadline has passed (for grace period handling)
  const now = new Date();
  const hasFreeCancellationPassed = now > freeCancellationDate;
  const hasModificationDeadlinePassed = now > modifyParticipantsDate;

  const localeCode = i18n.resolvedLanguage || i18n.language || "en";
  const normalizedLocale = localeCode.split("-")[0].toLowerCase();
  const dateLocaleMap: Record<string, string> = {
    sv: "sv-SE",
    da: "da-DK",
    no: "nb-NO",
    en: "en-US",
  };
  const dateLocale = dateLocaleMap[normalizedLocale] || localeCode;

  const isSeparator = (value: string) =>
    /[\s\u2013\u2014.,/()\-]/.test(value);
  const toTitleCase = (value: string) => {
    let result = "";
    let shouldCapitalize = true;
    for (const char of value) {
      const hasCase =
        char.toLocaleLowerCase(dateLocale) !==
        char.toLocaleUpperCase(dateLocale);
      if (shouldCapitalize && hasCase) {
        result += char.toLocaleUpperCase(dateLocale);
      } else {
        result += char;
      }
      shouldCapitalize = isSeparator(char);
    }
    return result;
  };

  const formatDisplayDate = (
    date: Date,
    options: Intl.DateTimeFormatOptions,
  ) => {
    const formatted = date.toLocaleDateString(dateLocale, options);
    return toTitleCase(formatted);
  };

  const formatPolicyDate = (date: Date) =>
    formatDisplayDate(date, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  // Calculate pricing using PricingService
  const calculatePricing = () => {
    // The showData.price already contains the final per-person price with modifiers
    // For private customers, it includes VAT; for corporate, it doesn't
    const isPrivate = bookingType !== "company";
    const pricePerPerson = showData.price || 0;
    const vatRate = showData.vat_percentage || 25;

    // Use minimum charge participants if applicable
    const chargedParticipants = showData?.min_charge_participants &&
                                showData.min_charge_participants > 0 &&
                                showData.min_charge_participants > currentGroupSize
                                ? showData.min_charge_participants
                                : currentGroupSize;

    // For private: price includes VAT, so we need to extract the base
    // For corporate: price excludes VAT
    const basePerPerson = isPrivate
      ? pricePerPerson / (1 + vatRate / 100) // Extract base from VAT-inclusive price (112.5 / 1.25 = 90)
      : pricePerPerson; // Already VAT-exclusive for corporate

    const subtotalWithoutVAT = basePerPerson * chargedParticipants; // Base amount without VAT
    const vatAmount = subtotalWithoutVAT * (vatRate / 100); // VAT amount
    const totalWithVAT = subtotalWithoutVAT + vatAmount; // Total including VAT

    // Return simplified format for backward compatibility
    return {
      pricePerPerson: pricePerPerson, // Per-person price (incl. VAT for private)
      pricePerPersonInclVat: pricePerPerson, // Same as above
      subtotal: subtotalWithoutVAT, // Subtotal WITHOUT VAT for display
      vatAmount: vatAmount, // VAT amount
      total: totalWithVAT, // Total including VAT
      priceWithoutVat: basePerPerson,
      // Additional fields - corporate shows excl. VAT, private shows incl. VAT
      displayAmount: isPrivate ? totalWithVAT : subtotalWithoutVAT,
      displayLabel: isPrivate ? "incl. VAT" : "excl. VAT",
    };
  };

  const pricing = calculatePricing();
  const pricingSummary = useMemo<BookingSummaryEditPricing>(() => {
    if (editConfig) {
      return editConfig.pricing;
    }
    // Determine if minimum charge applies
    const actualParticipants = currentGroupSize;
    const chargedParticipants = showData?.min_charge_participants &&
                               showData.min_charge_participants > 0 &&
                               showData.min_charge_participants > currentGroupSize
                               ? showData.min_charge_participants
                               : currentGroupSize;

    return {
      pricePerPerson:
        bookingType === "company"
          ? pricing.pricePerPerson
          : pricing.pricePerPersonInclVat,
      participants: actualParticipants, // Show actual participants selected
      subtotal: pricing.subtotal,
      vatAmount: pricing.vatAmount,
      total: bookingType === "company" ? pricing.displayAmount : pricing.total,
      originalTotal: undefined,
      difference: undefined,
    };
  }, [editConfig, bookingType, pricing, currentGroupSize]);
  const currencyCode = getCurrencySymbol(showData.currency || "SEK");
  const formatCurrencyValue = (value: number) =>
    `${value.toLocaleString()} ${currencyCode}`;

  // Format time display - prioritize user's selected language, fallback to venue's default language
  const locale = i18n.language || showData.venueDefaultLanguage || showData.venue_info?.defaultLanguage || "en";

  // Convert API data to ShowData type for modal/sheet components
  // The showData.price already includes VAT for private bookings
  const modalPrice = showData.price; // Already has correct price with modifiers and VAT from API

  const convertedShow: ShowData = {
    id: showData.id,
    name: showData.name,
    subtitle: showData.subtitle || "",
    description: showData.description || "",
    startTime: time ? time.substring(0, 5) : "10:00",
    duration: showData.duration,
    price: modalPrice, // Use the price from API directly
    currency: showData.currency,
    min_participants: showData.min_participants || 1,
    max_participants: showData.max_participants || 10,
    min_charge_participants: showData.min_charge_participants || null,
    language: showData.language || "en",
    has_booking_conflict: showData.has_booking_conflict || false,
    images: showData.media?.image_urls || [],
    videos: showData.media?.video_urls || [],
    faqs: showData.faqs || [],
    venue_info: showData.venue_info || { name: null, description: null },
    minimumCharge: showData.price * 0.8,
    groupSize: `${showData.min_participants || 1}-${showData.max_participants || 10} people`,
    availableLanguages: showData.availableLanguages || ["en"], // Add default available languages
  };

  const [showDetails, setShowDetails] = useState(false);

  const formattedDate = formatDisplayDate(selectedDate, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Gap warning modal handlers
  const handleGapWarningCancel = () => {
    setGapWarningOpen(false);
    setGapWarningData(null);
  };

  // Check for booking gaps before confirming
  const checkBookingGapBeforeConfirm = async (): Promise<boolean> => {
    if (!email) {
      return true;
    }

    try {

      const dateStr = selectedDate.toISOString().split("T")[0];
      const response = await fetch("/api/bookings/check-gap", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          showDate: dateStr,
          startTime: time || "10:00",
          duration: 60,
          venueId: venueInfo.id,
        }),
      });

      const result = await response.json();

      // If there's a conflict, show the warning modal
      if (result.hasConflict && result.conflictingBooking) {
        setGapWarningData(result.conflictingBooking);
        setGapWarningOpen(true);
        return false; // Block booking until user confirms
      }

      // No conflict, allow booking to proceed
      return true;
    } catch (error) {
      console.error("❌ Error checking booking gap:", error);
      // On error, allow booking to proceed
      return true;
    }
  };

  // Handle form submission
  const handleConfirmBooking = async (ignoreGapWarning: boolean = false) => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    // Validate required fields
    if (!name || !email || !phone || !termsAccepted) {
      toast.error(
        t(
          "summary.contact.requiredFields",
          "Please fill in all required fields and accept the terms.",
        ),
      );
      return;
    }

    // Validate email format
    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError("Please enter a valid email address");
        return;
      }
    }

    // Validate phone format
    if (phone) {
      const phoneResult = validatePhone(phone);
      if (!phoneResult.isValid) {
        setPhoneError(phoneResult.error || "Invalid phone number");
        return;
      }
    }

    // Check for booking gaps (unless user is confirming despite gap warning)
    if (!ignoreGapWarning) {
      const canProceed = await checkBookingGapBeforeConfirm();
      if (!canProceed) {
        return;
      }
    }

    // Validate language selection (required when multiple languages available)
    if (sortedAvailableLanguages.length > 0 && !language) {
      toast.error(
        t(
          "summary.languagePreferences.pleaseSelect",
          "Please select a language for your Game Show.",
        ),
      );
      return;
    }

    // Additional validation for company bookings - simplified
    if (bookingType === "company") {
      if (!companyName) {
        toast.error("Please enter your company name.");
        return;
      }
    }

    // Start loading state
    setIsSubmitting(true);

    try {
      // Format phone to E.164 before sending to backend
      const formattedPhone = phone ? formatPhoneToE164(phone) : "";

      // Facebook Pixel: Track InitiateCheckout event
      await trackInitiateCheckout(
        venueInfo.id,
        pricing.total,
        currentGroupSize,
        showData.currency || 'DKK'
      ).catch(err => {});
      // Intelligently select an available studio
      let selectedStudioId = showData.studios?.[0]?.id || 1; // Default to first studio

      // Use available studio IDs from summary data if provided
      if (showData.available_studio_ids && showData.available_studio_ids.length > 0) {
        // Pick the first available studio
        selectedStudioId = showData.available_studio_ids[0];
      } else if (showData.studios && showData.studios.length > 0) {
        // Fall back to first studio if no availability data
        selectedStudioId = showData.studios[0].id;
      }

      // Prepare booking data
      const bookingData = {
        // Personal info
        name,
        email,
        phone: formattedPhone,

        // Booking details
        showId: showData.id,
        venueId: venueInfo.id,
        studioId: selectedStudioId,
        participants: currentGroupSize,

        // Pricing information
        assignedPricingId: showData.pricing_id || 1, // Use pricing_id (actual pricing record) not price_point_id
        showTimeSlotPricingId: showData.show_time_slot_pricing_id || 1, // Use the correct time slot ID
        pricePointId: showData.price_point_id || 1,
        totalPrice: pricing.total,
        currency: showData.currency || "DKK",

        // Session information
        showSessionTimeslot: `${selectedDate.toISOString().split("T")[0]}T${time}+00:00`,

        // Additional context
        date: selectedDate.toISOString().split("T")[0],
        time: time,
        dayOfWeek: selectedDate.getDay(),

        // Company info (if applicable - simplified)
        ...(bookingType === "company" && {
          companyName,
        }),

        // Additional details
        language: language || undefined,
        bookingType,

        // Gap warning override
        ...(ignoreGapWarning && {
          ignoreGapWarning: true,
        }),
      };

      // Call the appropriate booking API based on booking type
      const apiEndpoint =
        bookingType === "company"
          ? "/api/bookings/create-corporate"
          : "/api/bookings/create-private";

      let response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingData),
      });

      let result = await response.json();

      // If API says no session (new user), create anonymous session and retry
      if (!result.success && result.code === "NO_SESSION" && !currentUserEmail) {
        const supabase = createClient();
        const { error: anonError } = await supabase.auth.signInAnonymously();
        if (anonError) {
          console.error("Failed to create anonymous session:", anonError);
          toast.error("Failed to initialize booking session. Please try again.");
          setIsSubmitting(false);
          return;
        }

        // Retry with the new anonymous session cookies
        response = await fetch(apiEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bookingData),
        });
        result = await response.json();
      }

      if (result.success) {

        // PostHog: Identify user and capture booking_created event
        posthog.identify(email, {
          email: email,
          name: name,
        });
        posthog.capture("booking_created", {
          booking_id: result.data?.bookingId,
          booking_type: bookingType,
          show_id: showData.id,
          show_name: showData.name,
          venue_id: venueInfo?.id,
          venue_name: venueInfo?.name,
          participants: currentGroupSize,
          total_price: pricing.total,
          currency: showData.currency,
          date: selectedDate.toISOString().split("T")[0],
          time: time,
          is_new_user: result.isNewUser,
          language: language,
        });

        // SIMPLIFIED: Both new and existing users use identical navigation
        // Server-side session cookies handle all authentication

        // Navigate to confirmation page - IDENTICAL logic for both user types
        const redirectUrl =
          result.data.redirectTo ||
          `/bookings/confirmation/${result.data.bookingId}`;

        // Store email in sessionStorage for the confirmation page
        if (typeof window !== "undefined" && email) {
          sessionStorage.setItem("bookingEmail", email);
        }

        // Immediate navigation - no delay needed
        try {
          router.push(redirectUrl);
        } catch (navError) {
          console.error("❌ [DEBUG] Navigation failed:", navError);
          // Fallback navigation
          window.location.href = redirectUrl;
        }
      } else {
        console.error("❌ [DEBUG] Booking API returned failure");
        console.error("❌ [DEBUG] Error message:", result.error);
        console.error("❌ [DEBUG] Error code:", result.code);
        console.error("❌ [DEBUG] Full error response:", result);

        // Reset loading state on failure
        setIsSubmitting(false);

        // Handle specific error cases
        if (result.conflictDetails) {
          // Show the gap warning modal
          setGapWarningData(result.conflictDetails);
          setGapWarningOpen(true);
        } else if (result.code === "EMAIL_EXISTS") {
          toast.error(result.error || "This email already exists. Please login or use a different email.");
          // Optionally redirect to login
          // window.location.href = '/auth/login?email=' + encodeURIComponent(email);
        } else if (result.code === "STAFF_ROLE_RESTRICTION" ||
                   result.error?.includes("Staff members cannot create bookings") ||
                   result.error?.includes("must reference a customer") ||
                   result.error?.includes("role_id")) {
          toast.error("Staff members (Super Admin, Booking Admin, Bar Staff) cannot create bookings through the customer portal.");
        } else {
          toast.error(result.error || "Booking failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("💥 [DEBUG] Exception in booking process:", error);
      if (error instanceof Error) {
        console.error("💥 [DEBUG] Exception stack:", error.stack);
        console.error("💥 [DEBUG] Exception details:", {
          name: error.name,
          message: error.message,
        });
      }
      if (error instanceof Error) {
        console.error("💥 [DEBUG] Exception stack:", error.stack);
        console.error("💥 [DEBUG] Exception details:", {
          name: error.name,
          message: error.message,
        });
      }
      // PostHog: Capture booking error
      posthog.captureException(error);

      // Reset loading state on exception
      setIsSubmitting(false);

      toast.error("An error occurred while creating your booking. Please try again.");
    }
  };

  const handleEditConfirm = async () => {
    if (!editConfig) return;
    try {
      await editConfig.onConfirm();
      setEditDialogOpen(false);
    } catch (error) {
      console.error("💥 [DEBUG] Error updating booking:", error);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const response = await fetch("/api/auth/logout", {
        method: "POST",
      });

      if (response.ok) {
        // Refresh the page to clear the auth state
        window.location.reload();
      } else {
        toast.error("Failed to logout. Please try again.");
        setIsLoggingOut(false);
      }
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to logout. Please try again.");
      setIsLoggingOut(false);
    }
  };

  const handlePrimaryAction = () => {
    if (isEditMode) {
      if (!termsAccepted) {
        toast.error(
          t(
            "summary.contact.termsRequired",
            "Please accept the terms to continue.",
          ),
        );
        return;
      }
      setEditDialogOpen(true);
      return;
    }
    handleConfirmBooking();
  };

  return (
    <section className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm hover:text-primary bg-transparent border-none p-0 outline-none focus:outline-none flex-shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t("summary.actions.back", "Back")}
              </span>
            </button>

            <div className="absolute left-1/2 transform -translate-x-1/2">
              <img
                src="https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/on-air-logo.png"
                alt="On Air Game Shows"
                className="h-8 sm:h-9 md:h-10 w-auto p-2 rounded-lg shadow-md"
                style={{
                  backgroundImage:
                    "url(https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/footer-background-sm.jpg)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                <span className="hidden sm:inline">
                  {t("summary.step", "Step 5 of 5")}
                </span>
                <span className="sm:hidden text-xs">5/5</span>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                </div>
              </div>
              <LanguageSelector availableLanguages={availableLanguages} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold">
            {t("summary.title", "Review & Confirm")}
          </h1>
        </div>
        {/* Mobile: Custom order | Desktop: Standard 2-column layout */}
        <div className="flex flex-col gap-8 lg:grid lg:grid-cols-[2fr_1fr] lg:gap-8">
          {/* Forms Section - contains all form cards */}
          <div className="contents lg:block">
            {/* Initial Forms Group (Language, Add-ons, Group Size, Policies) - order 1 on mobile */}
            <div className="space-y-6 order-1 lg:order-none">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t(
                    "summary.languagePreferences.title",
                    "Language Preferences",
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {t(
                    "summary.languagePreferences.subtitle",
                    "Select your preferred language for the experience",
                  )}
                </p>
              </CardHeader>
              <CardContent>
                {sortedAvailableLanguages && sortedAvailableLanguages.length > 0 ? (
                  <>
                    <RadioGroup
                      value={language}
                      onValueChange={setLanguage}
                      className={`grid ${sortedAvailableLanguages.length <= 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"} gap-4`}
                    >
                      {sortedAvailableLanguages.map((lang) => (
                        <div key={lang} className="flex-1">
                          <RadioGroupItem
                            value={lang}
                            id={lang}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={lang}
                            className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                          >
                            <span className="text-base font-semibold">
                              {getLanguageLabel(lang)}
                            </span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {language ? (
                      <p className="text-xs text-muted-foreground mt-3">
                        {t(
                          "summary.languagePreferences.conductedIn",
                          "Your experience will be conducted in {{language}}.",
                          { language: getLanguageLabel(language) },
                        )}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 mt-3">
                        {t(
                          "summary.languagePreferences.pleaseSelect",
                          "Please select a language for your experience.",
                        )}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "summary.languagePreferences.availableIn",
                      "This experience is available in English.",
                    )}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t("summary.addons.title", "Add-ons")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "summary.addons.subtitle",
                    "You'll be able to select your add-ons after confirming your booking",
                  )}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t("summary.groupSize.title", "How many are you?")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm mb-2 block">
                      <Users className="h-3.5 w-3.5 inline mr-2" />
                      {t(
                        "summary.groupSize.participants",
                        "Group size: {{count}} participants",
                        { count: pricingSummary.participants },
                      )}
                    </Label>
                    <Slider
                      min={minParticipants}
                      max={maxParticipants}
                      step={1}
                      value={[currentGroupSize]}
                      onValueChange={([value]) => setCurrentGroupSize(value)}
                      onValueCommit={([value]) =>
                        editConfig?.onParticipantsChange?.(value)
                      }
                      className="w-full"
                      disabled={
                        editConfig?.onParticipantsChange &&
                        editConfig.isProcessing
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "summary.groupSize.canChange",
                        "Don't worry, you can easily change how many people up to {{days}} days before the show.",
                        { days: partChangeFullRefundDays }
                      )}
                    </p>
                    {showData?.min_charge_participants &&
                     showData.min_charge_participants > 0 &&
                     showData.min_charge_participants > currentGroupSize && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        * {t(
                          "summary.groupSize.minimumCharge",
                          "Minimum charge for {{count}} participants applies",
                          { count: showData.min_charge_participants }
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6 lg:mb-0">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  <CardTitle className="text-lg">
                    {t("summary.policies.title", "Booking Policies")}
                  </CardTitle>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t(
                    "summary.policies.subtitle",
                    "Cancellation & Modification Policy",
                  )}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Mobile: Stack vertically, Desktop: Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col items-center p-3 sm:p-4 rounded-lg bg-background border text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50 text-green-600 text-sm font-semibold mb-2">
                        1
                      </span>
                      <h4 className="font-medium text-sm mb-1">
                        {t(
                          "summary.policies.freeCancellation",
                          "Free Cancellation",
                        )}
                      </h4>
                      {hasFreeCancellationPassed ? (
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "summary.policies.gracePeriod",
                            "Within {{hours}} hours of booking",
                            { hours: cancelGraceHours },
                          )}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {t("summary.policies.until", "Until {{date}}", {
                            date: formatPolicyDate(freeCancellationDate),
                          })}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-center p-3 sm:p-4 rounded-lg bg-background border text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-2">
                        2
                      </span>
                      <h4 className="font-medium text-sm mb-1">
                        {t(
                          "summary.policies.modifyParticipants",
                          "Modify Participants",
                        )}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {hasModificationDeadlinePassed ? (
                          t(
                            "summary.policies.gracePeriod",
                            "Within {{hours}} hours of booking",
                            { hours: cancelGraceHours },
                          )
                        ) : (
                          t("summary.policies.until", "Until {{date}}", {
                            date: formatPolicyDate(modifyParticipantsDate),
                          })
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-center p-3 sm:p-4 rounded-lg bg-background border text-center sm:col-span-2 lg:col-span-1">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-50 text-purple-600 text-sm font-semibold mb-2">
                        3
                      </span>
                      <h4 className="font-medium text-sm mb-1">
                        {t("summary.policies.payment", "Payment")}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {bookingType === "company"
                          ? t(
                              "summary.policies.invoicePayment",
                              "Invoice payment",
                            )
                          : t("summary.policies.cardOrCash", "Card")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 sm:p-4 rounded-lg bg-secondary/50">
                    <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground text-left">
                      {t(
                        "summary.policies.additionalRestrictions",
                        "After these dates, additional restrictions apply. See our",
                      )}{" "}
                      <a
                        href={`/venues/${venueInfo?.id || showData?.venue_id}/terms?lang=${i18n.language}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {t("summary.policies.fullTerms", "full terms")}
                      </a>{" "}
                      {t("summary.policies.forDetails", "for details.")}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>

            {/* Company and Contact Forms Group - order 3 on mobile */}
            <div className="space-y-6 order-3 lg:order-none pt-8 lg:pt-0">
              {/* Company Information - Show for all company bookings */}
            {bookingType === "company" && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t("summary.company.title", "Company Booking")}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isEditMode
                      ? t(
                          "summary.company.subtitleEdit",
                          "Company information for this booking",
                        )
                      : t(
                          "summary.company.subtitle",
                          "Basic info now, complete invoice details later",
                        )}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">
                      {t("summary.company.companyName", "Company Name")} *
                    </Label>
                    <Input
                      id="companyName"
                      value={
                        companyName ||
                        editConfig?.contactInfo?.companyName ||
                        ""
                      }
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={t(
                        "summary.company.companyNamePlaceholder",
                        "Enter your company name",
                      )}
                      required
                      readOnly={isEditMode && editConfig?.contactInfo?.readOnly}
                      disabled={isEditMode && editConfig?.contactInfo?.readOnly}
                      className={
                        isEditMode && editConfig?.contactInfo?.readOnly
                          ? "bg-muted"
                          : ""
                      }
                    />
                  </div>

                  {!isEditMode && (
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "summary.company.invoiceNote",
                          "📝 You'll be able to add complete invoice details (organization number, billing address, VAT number, etc.) after booking in your customer portal.",
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {bookingType === "company"
                    ? t("summary.contact.titleCompany", "Contact Person")
                    : t("summary.contact.title", "Contact Information")}
                </CardTitle>
                {bookingType === "company" && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {t(
                      "summary.contact.subtitleCompany",
                      "Primary contact person for this booking",
                    )}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t("summary.contact.fullName", "Full Name")} *
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={
                      bookingType === "company"
                        ? t(
                            "summary.contact.nameCompanyPlaceholder",
                            "Enter contact person name",
                          )
                        : t(
                            "summary.contact.namePlaceholder",
                            "Enter your full name",
                          )
                    }
                    required
                    readOnly={contactReadOnly}
                    disabled={contactReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="email">
                      {t("summary.contact.email", "Email")} *
                    </Label>
                    {isLoggedIn && !isEditMode && (
                      <button
                        type="button"
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className="text-xs text-primary hover:underline disabled:opacity-50"
                      >
                        {isLoggingOut
                          ? t("summary.contact.loggingOut", "Logging out...")
                          : t(
                              "summary.contact.logoutLink",
                              "Click here to logout & use another email",
                            )}
                      </button>
                    )}
                  </div>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    placeholder={
                      bookingType === "company"
                        ? t(
                            "summary.contact.emailCompanyPlaceholder",
                            "Enter contact person email",
                          )
                        : t(
                            "summary.contact.emailPlaceholder",
                            "Enter your email",
                          )
                    }
                    required
                    readOnly={contactReadOnly || isLoggedIn}
                    disabled={contactReadOnly || isLoggedIn}
                    className={
                      contactReadOnly || isLoggedIn ? "bg-muted" : emailError ? "border-red-500" : ""
                    }
                  />
                  {emailError && (
                    <p className="text-sm text-red-500 mt-1">{emailError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    {t("summary.contact.phone", "Phone Number")} *
                  </Label>
                  <PhoneInput
                    id="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(201) 555-0123"
                    required
                    disabled={contactReadOnly}
                    readOnly={contactReadOnly}
                    error={!!phoneError}
                    defaultCountry={getDefaultCountryFromTimezone(venueInfo?.timezone, "US")}
                  />
                  {phoneError ? (
                    <p className="text-sm text-red-500 mt-1">{phoneError}</p>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1">
                      {t("summary.contact.phoneHelp", "Enter a valid phone number")}
                    </p>
                  )}
                </div>

                <div className="flex items-start space-x-2 pt-4">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Label htmlFor="terms" className="text-sm">
                    {t("summary.contact.terms", "I accept the")}{" "}
                    <a
                      href={`/venues/${venueInfo?.id || showData?.venue_id}/terms?lang=${i18n.language}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t("summary.contact.termsLink", "terms and conditions")}
                    </a>
                  </Label>
                </div>

                <Button
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 mt-4"
                  size="lg"
                  disabled={
                    !termsAccepted || isSubmitting || (isEditMode && editConfig?.isProcessing) || !!phoneError || !!emailError
                  }
                  onClick={handlePrimaryAction}
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isSubmitting
                    ? t("summary.actions.processing", "Processing...")
                    : isEditMode
                    ? t("summary.actions.modifyBooking", "Modify booking")
                    : t("summary.actions.confirmBooking", "Confirm Booking")}
                </Button>
              </CardContent>
            </Card>
            </div>
          </div>

          {/* Booking Summary Section - order 2 on mobile, right column on desktop */}
          <div className="space-y-6 order-2 lg:order-none lg:sticky lg:top-24">
            <Card
              className={isEditMode ? "border-purple-200 bg-purple-50/50" : ""}
            >
              <CardHeader>
                <CardTitle className="text-lg">
                  {t("summary.bookingSummary.title", "Booking Summary")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold">{showData.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {showData.subtitle}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{time ? formatTimeDisplay(time, locale) : "TBD"}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{showData.duration}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {t("summary.bookingSummary.people", "{{count}} people", {
                        count: currentGroupSize,
                      })}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{venueInfo?.name || "Selected Venue"}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {showData.price?.toFixed(2)} {currencyCode}
                      {t("summary.bookingSummary.perPerson", "/person")}
                      {bookingType === "company"
                        ? " (" +
                          t(
                            "summary.bookingSummary.excludingVat",
                            "excl. VAT",
                          ) +
                          ")"
                        : " (" +
                          t(
                            "summary.bookingSummary.includingVat",
                            "incl. VAT",
                          ) +
                          ")"}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <span>{formattedDate}</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full text-sm"
                  onClick={() => setShowDetails(true)}
                >
                  {t("summary.bookingSummary.moreDetails", "More details")}
                </Button>
              </CardContent>
            </Card>

            {isEditMode && editConfig && (
              <Card className="border-gray-200 bg-gray-50/50">
                <CardHeader>
                  <CardTitle className="text-base font-medium text-muted-foreground">
                    {t(
                      "summary.bookingSummary.detailsBeforeModification",
                      "Details Before Modification",
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  {/* Show company name for corporate bookings */}
                  {bookingType === "company" &&
                    editConfig.contactInfo?.companyName && (
                      <SummaryRow
                        icon={<Users className="h-4 w-4 text-primary" />}
                        label={t(
                          "summary.bookingSummary.companyName",
                          "Company",
                        )}
                        value={editConfig.contactInfo.companyName}
                      />
                    )}
                  <SummaryRow
                    icon={<MapPin className="h-4 w-4 text-primary" />}
                    label={t("summary.bookingSummary.originalShow", "Show")}
                    value={editConfig.originalDetails.showName}
                  />
                  <SummaryRow
                    icon={<Clock className="h-4 w-4 text-primary" />}
                    label={t(
                      "summary.bookingSummary.originalTime",
                      "Time slot",
                    )}
                    value={editConfig.originalDetails.timeSlot}
                  />
                  <SummaryRow
                    icon={<CalendarDays className="h-4 w-4 text-primary" />}
                    label={t("summary.bookingSummary.originalDate", "Date")}
                    value={editConfig.originalDetails.date}
                  />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  {t(
                    "summary.bookingSummary.paymentSummary",
                    "Payment Summary",
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t(
                      "summary.bookingSummary.pricePerPerson",
                      "Price per person",
                    )}
                    <span className="text-xs ml-1">
                      (
                      {bookingType === "company"
                        ? t("summary.bookingSummary.excludingVat", "excl. VAT")
                        : t("summary.bookingSummary.includingVat", "incl. VAT")}
                      )
                    </span>
                  </span>
                  <span>
                    {formatCurrencyValue(pricingSummary.pricePerPerson)}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t(
                      "summary.bookingSummary.participants",
                      "Number of participants",
                    )}
                  </span>
                  <span>× {pricingSummary.participants}</span>
                </div>

                {showData?.min_charge_participants &&
                 showData.min_charge_participants > 0 &&
                 showData.min_charge_participants > pricingSummary.participants && (
                  <div className="flex justify-between text-xs text-amber-600 dark:text-amber-400">
                    <span>
                      * {t(
                        "summary.bookingSummary.minimumCharge",
                        "Minimum {{count}} participants charged",
                        { count: showData.min_charge_participants }
                      )}
                    </span>
                    <span>× {showData.min_charge_participants}</span>
                  </div>
                )}

                {bookingType === "company" ? (
                  /* Corporate: Show excl VAT subtotal and note about invoice */
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t("summary.bookingSummary.subtotal", "Subtotal")}
                      </span>
                      <span>{formatCurrencyValue(pricingSummary.subtotal)}</span>
                    </div>

                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>
                        {t(
                          "summary.bookingSummary.vatNote",
                          "VAT will be added on invoice",
                        )}
                      </span>
                      <span>{formatCurrencyValue(pricingSummary.vatAmount)}</span>
                    </div>
                  </>
                ) : (
                  /* Private: Show incl VAT subtotal with breakdown */
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t(
                          "summary.bookingSummary.subtotalInclVat",
                          "Subtotal (incl. VAT)"
                        )}
                      </span>
                      <span>{formatCurrencyValue(pricingSummary.total)}</span>
                    </div>

                    <div className="flex justify-between text-sm text-muted-foreground italic pl-4">
                      <span>
                        {t(
                          "summary.bookingSummary.ofWhichVat",
                          "of which VAT ({{percentage}}%)",
                          {
                            percentage: showData.vat_percentage || 25,
                          },
                        )}
                      </span>
                      <span>{formatCurrencyValue(pricingSummary.vatAmount)}</span>
                    </div>
                  </>
                )}

                <Separator />

                <div className="flex justify-between font-semibold">
                  <span>
                    {bookingType === "company"
                      ? t(
                          "summary.bookingSummary.totalExclVat",
                          "Total (excl. VAT)",
                        )
                      : t("summary.bookingSummary.total", "Total (incl. VAT)")}
                  </span>
                  <span>{formatCurrencyValue(pricingSummary.total)}</span>
                </div>

                {editConfig?.pricing.originalTotal !== undefined && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>
                      {t(
                        "summary.bookingSummary.originalTotal",
                        "Original total",
                      )}
                    </span>
                    <span
                      className={
                        editConfig.pricing.difference ? "line-through" : ""
                      }
                    >
                      {formatCurrencyValue(editConfig.pricing.originalTotal)}
                    </span>
                  </div>
                )}

                {editConfig?.pricing.difference !== undefined &&
                  editConfig.pricing.difference !== 0 && (
                    <div className="flex justify-between text-sm font-medium text-foreground">
                      <span>
                        {editConfig.pricing.difference > 0
                          ? t(
                              "summary.bookingSummary.additionalAmount",
                              "Additional amount",
                            )
                          : t(
                              "summary.bookingSummary.refundAmount",
                              "Refund amount",
                            )}
                      </span>
                      <span>
                        {formatCurrencyValue(
                          Math.abs(editConfig.pricing.difference),
                        )}
                      </span>
                    </div>
                  )}

                <Separator className="mt-4" />
                <p className="text-xs text-muted-foreground text-center">
                  {t(
                    "summary.bookingSummary.confirmNote",
                    "Please confirm your details and accept terms to complete booking",
                  )}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Render the appropriate variant based on device type */}
      {showDetails &&
        (isMobile ? (
          <SheetVariantFour
            show={convertedShow}
            date={selectedDate}
            open={showDetails}
            onOpenChange={setShowDetails}
            onBookNow={() => setShowDetails(false)} // Close modal, already on summary page
            bookingType={bookingType}
          />
        ) : (
          <ModalVariantThree
            show={convertedShow}
            date={selectedDate}
            open={showDetails}
            onOpenChange={setShowDetails}
            onBookNow={() => setShowDetails(false)} // Close modal, already on summary page
            bookingType={bookingType}
          />
        ))}

      {isEditMode && editConfig && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {t("summary.bookingSummary.modifyTitle", "Modify Your Booking")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "summary.bookingSummary.modifySubtitle",
                  "Review the changes below before updating your booking.",
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 text-sm text-muted-foreground">
              {/* Before Section */}
              <div>
                <h4 className="font-semibold text-foreground">
                  {t(
                    "summary.bookingSummary.detailsBeforeModification",
                    "Details Before Modification",
                  )}
                </h4>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span>
                      {t("summary.bookingSummary.originalShow", "Show")}
                    </span>
                    <span className="font-medium text-foreground">
                      {editConfig.originalDetails.showName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {t("summary.bookingSummary.originalTime", "Time slot")}
                    </span>
                    <span className="font-medium text-foreground">
                      {editConfig.originalDetails.timeSlot}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {t("summary.bookingSummary.originalDate", "Date")}
                    </span>
                    <span className="font-medium text-foreground">
                      {editConfig.originalDetails.date}
                    </span>
                  </div>
                </div>
              </div>

              {/* After Section */}
              <div>
                <h4 className="font-semibold text-foreground">
                  {t(
                    "summary.bookingSummary.detailsAfterModification",
                    "Details After Modification",
                  )}
                </h4>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span>
                      {t("summary.bookingSummary.newShow", "Show")}
                    </span>
                    <span className="font-medium text-primary">
                      {showData.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {t("summary.bookingSummary.newTime", "Time slot")}
                    </span>
                    <span className="font-medium text-primary">
                      {formatTimeDisplay(time, locale)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {t("summary.bookingSummary.newDate", "Date")}
                    </span>
                    <span className="font-medium text-primary">
                      {selectedDate.toLocaleDateString(locale || 'en', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-foreground">
                  {t(
                    "summary.bookingSummary.priceComparison",
                    "Price comparison",
                  )}
                </h4>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span>
                      {t(
                        "summary.bookingSummary.originalTotal",
                        "Original total",
                      )}
                    </span>
                    <span
                      className={
                        editConfig.pricing.difference ? "line-through" : ""
                      }
                    >
                      {formatCurrencyValue(
                        editConfig.pricing.originalTotal ??
                          editConfig.pricing.total,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between font-medium text-foreground">
                    <span>
                      {t(
                        "summary.bookingSummary.updatedTotal",
                        "Updated total",
                      )}
                    </span>
                    <span>{formatCurrencyValue(editConfig.pricing.total)}</span>
                  </div>
                  {editConfig.pricing.difference !== undefined &&
                    editConfig.pricing.difference !== 0 && (
                      <div className="flex justify-between">
                        <span>
                          {editConfig.pricing.difference > 0
                            ? t(
                                "summary.bookingSummary.additionalAmount",
                                "Additional amount",
                              )
                            : t(
                                "summary.bookingSummary.refundAmount",
                                "Refund amount",
                              )}
                        </span>
                        <span className="font-medium text-foreground">
                          {formatCurrencyValue(
                            Math.abs(editConfig.pricing.difference),
                          )}
                        </span>
                      </div>
                    )}
                </div>
              </div>

              {/* Add-on pricing notice */}
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>
                    {t("summary.bookingSummary.addonNotice", "Note:")}
                  </strong>{" "}
                  {t(
                    "summary.bookingSummary.addonPriceNotice",
                    "Price comparison is for the show only. Your existing add-ons remain unchanged and are not included in this price difference.",
                  )}
                </p>
              </div>
            </div>

            <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                disabled={editConfig.isProcessing}
              >
                {t("summary.bookingSummary.cancel", "Cancel")}
              </Button>
              <Button
                onClick={handleEditConfirm}
                disabled={editConfig.isProcessing}
              >
                {editConfig.isProcessing
                  ? t("summary.bookingSummary.updating", "Updating…")
                  : t(
                      "summary.bookingSummary.confirmUpdate",
                      "Yes, update booking",
                    )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Booking Gap Warning Modal */}
      <BookingGapWarningModal
        isOpen={gapWarningOpen}
        onCancel={handleGapWarningCancel}
        conflictingShow={gapWarningData || undefined}
      />
    </section>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span>{icon}</span>
      <div className="flex flex-col">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="text-sm text-foreground font-medium">{value}</span>
      </div>
    </div>
  );
}

function PolicyTile({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center p-3 sm:p-4 rounded-lg bg-background border text-center transition-colors hover:bg-accent">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-2">
        {step}
      </span>
      <h4 className="font-medium text-sm mb-1 text-foreground">{title}</h4>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}
